import { useEffect, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { ImageField } from "../components/block-fields";
import { api, siteApi } from "../api";
import type { Clock, Schedule, ScheduleWithBlocks, ScheduleBlockKind, Playlist, Show, ShowWithRelations, Moderator, Weekday } from "../types";
import * as ws from "../ws-client";
import { Badge, Callout, Card, EmptyState, Page, PageHeader } from "../components/ui";

const WEEKDAYS: { value: Weekday; label: string }[] = [
  { value: 0, label: "Mo" },
  { value: 1, label: "Di" },
  { value: 2, label: "Mi" },
  { value: 3, label: "Do" },
  { value: 4, label: "Fr" },
  { value: 5, label: "Sa" },
  { value: 6, label: "So" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export function SchedulePage(): JSX.Element {
  const { slug = "" } = useParams<{ slug: string }>();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [active, setActive] = useState<ScheduleWithBlocks | null>(null);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [shows, setShows] = useState<ShowWithRelations[]>([]);
  const [clocks, setClocks] = useState<Clock[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [renderOutput, setRenderOutput] = useState<string | null>(null);
  const [rightsWarnings, setRightsWarnings] = useState<{
    total_blocks: number;
    blocked_blocks: number;
    blocks: Array<{ id: string; weekday: number; start_time: string; end_time: string; title: string | null; blocking_items: Array<{ title: string; reasons: string[] }> }>;
  } | null>(null);

  useEffect(() => {
    if (!slug) return;
    Promise.all([
      api.listSchedules(slug).catch(() => []),
      api.listPlaylists(slug).catch(() => []),
      api.listShows().catch(() => []),
      api.listClocks().catch(() => []),
    ]).then(([schs, pls, shs, cls]) => {
      setClocks(cls);
      setSchedules(schs);
      setPlaylists(pls);
      setShows(shs);
      if (schs.length > 0) {
        api.getSchedule(schs[0].id).then(setActive).catch(() => setActive(null));
      }
    });
  }, [slug]);

  const selectSchedule = async (id: string): Promise<void> => {
    setBusy(true);
    try {
      const detail = await api.getSchedule(id);
      setActive(detail);
      setRenderOutput(null);
    } finally {
      setBusy(false);
    }
  };

  const createSchedule = async (): Promise<void> => {
    const name = prompt("Sendeplan-Name?");
    if (!name) return;
    try {
      const sch = await api.createSchedule(slug, { name, is_default: schedules.length === 0 });
      setSchedules([...schedules, sch]);
      selectSchedule(sch.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const addBlock = async (weekday: number, hour: number): Promise<void> => {
    if (!active) return;
    const startTime = `${String(hour).padStart(2, "0")}:00`;
    // Ein Block endet vor Mitternacht; 24:00 kennt die Datenbank nicht.
    const endTime = hour === 23 ? "23:59:59" : `${String(hour + 1).padStart(2, "0")}:00`;
    const firstShow = shows[0];
    try {
      await api.addScheduleBlock(active.id, {
        weekday,
        start_time: startTime,
        end_time: endTime,
        kind: firstShow ? "show" : "playlist",
        show_id: firstShow?.id,
        playlist_id: !firstShow ? playlists[0]?.id : undefined,
      });
      const detail = await api.getSchedule(active.id);
      setActive(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const removeBlock = async (blockId: string): Promise<void> => {
    try {
      await api.deleteScheduleBlock(blockId);
      if (active) {
        const detail = await api.getSchedule(active.id);
        setActive(detail);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const updateBlockField = async (blockId: string, patch: Record<string, unknown>): Promise<void> => {
    try {
      await api.updateScheduleBlock(blockId, patch);
      if (active) {
        const detail = await api.getSchedule(active.id);
        setActive(detail);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const renderSchedule = async (): Promise<void> => {
    if (!active) return;
    setBusy(true);
    try {
      const res = await api.renderSchedule(active.id);
      setRenderOutput(res.script);
    } finally {
      setBusy(false);
    }
  };

  const checkRights = async (): Promise<void> => {
    if (!active) return;
    try {
      const res = await api.getScheduleRightsCheck(active.id);
      setRightsWarnings({
        total_blocks: res.total_blocks,
        blocked_blocks: res.blocked_blocks,
        blocks: res.blocks.map((b) => ({
          id: b.id,
          weekday: b.weekday,
          start_time: b.start_time,
          end_time: b.end_time,
          title: b.title,
          blocking_items: b.blocking_items.map((it) => ({ title: it.title, reasons: it.reasons })),
        })),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const blocksAt = (weekday: number, hour: number): ScheduleWithBlocks["blocks"] => {
    if (!active) return [];
    return active.blocks.filter((b) => {
      if (b.weekday !== weekday) return false;
      const start = parseHour(b.start_time);
      const end = parseHour(b.end_time);
      return hour >= start && hour < end;
    });
  };

  // Farbe eines Blocks: die Farbe der Sendung bzw. Stundenuhr, sonst je Art.
  const blockColor = (b: ScheduleWithBlocks["blocks"][number]): string => {
    if (b.kind === "show") return shows.find((s) => s.id === b.show_id)?.default_color ?? "#ff5500";
    if (b.kind === "clock") return clocks.find((c) => c.id === b.clock_id)?.color ?? "#3b82f6";
    if (b.kind === "playlist") return "#10b981";
    if (b.kind === "live") return "#ef4444";
    return "#888888";
  };

  const miniSelect = "h-6 w-full rounded-md border bg-card px-1 py-0 text-[10px]";

  return (
    <Page className="max-w-[1600px]">
      <PageHeader
        eyebrow="Programm"
        title={<>Sendeplan <span className="font-normal text-muted">· {slug}</span></>}
        description="Wochenraster mit Drag-Pattern (Klick auf Stunde zum Anlegen)."
        actions={
          <>
            <select
              className="input h-9 w-auto min-w-[10rem]"
              value={active?.id ?? ""}
              onChange={(e) => selectSchedule(e.target.value)}
              disabled={schedules.length === 0}
              aria-label="Sendeplan wählen"
            >
              {schedules.length === 0 && <option>Keine Sendepläne</option>}
              {schedules.map((s) => (
                <option key={s.id} value={s.id}>{s.name}{s.is_default ? " (default)" : ""}</option>
              ))}
            </select>
            {active && (
              <>
                <button onClick={renderSchedule} disabled={busy} className="btn btn-secondary">
                  So sendet die Engine
                </button>
                <button onClick={checkRights} disabled={busy} className="btn btn-secondary text-amber-700 dark:text-amber-300">
                  Rechte prüfen
                </button>
              </>
            )}
            <button onClick={createSchedule} className="btn btn-primary">
              + Sendeplan
            </button>
          </>
        }
      />

      {error && <Callout tone="danger" className="mb-4">{error}</Callout>}

      {active && (
        <div className="space-y-4">
          {/* Legende: welche Farbe was bedeutet */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
            {([["Sendung", "#ff5500"], ["Stundenuhr", "#3b82f6"], ["Playlist", "#10b981"], ["Live", "#ef4444"]] as const).map(([l, c]) => (
              <span key={l} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: c }} />{l}</span>
            ))}
            <span className="ml-auto hidden sm:inline">Leere Stunde anklicken, um einen Block anzulegen.</span>
          </div>

          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] table-fixed text-xs">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 w-16 border-b border-line bg-surface-sunken p-2 text-left">Zeit</th>
                    {WEEKDAYS.map((d) => (
                      <th key={d.value} className="border-b border-l border-line bg-surface-sunken p-2 text-center">{d.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {HOURS.map((h) => (
                    <tr key={h} className="hover:!bg-transparent">
                      <td className="sticky left-0 z-10 border-b border-line bg-card px-2 py-1 align-top font-mono text-[11px] tabular-nums text-muted">{String(h).padStart(2, "0")}:00</td>
                      {WEEKDAYS.map((d) => {
                        const blocks = blocksAt(d.value, h);
                        return (
                          <td
                            key={`${d.value}-${h}`}
                            className={`h-9 border-b border-l border-line p-1 align-top ${blocks.length === 0 ? "group cursor-pointer hover:bg-brand-primary/5" : ""}`}
                            onClick={() => blocks.length === 0 && addBlock(d.value, h)}
                          >
                            {blocks.length === 0 && (
                              <span className="flex h-full items-center justify-center text-sm text-brand-primary opacity-0 transition group-hover:opacity-100" aria-hidden="true">+</span>
                            )}
                            {blocks.map((b) => {
                              const farbe = blockColor(b);
                              // Läuft der Block schon seit einer früheren Stunde, reicht eine
                              // schmale Fortsetzung – bedient wird er in seiner ersten Stunde.
                              if (parseHour(b.start_time) !== h) {
                                const name = b.kind === "show" ? b.show_title : b.kind === "playlist" ? b.playlist_name : null;
                                return (
                                  <div
                                    key={b.id}
                                    className="mb-1 flex min-h-[1.5rem] items-center truncate rounded-md border-l-[3px] px-1.5 text-[10px] text-muted last:mb-0"
                                    style={{ borderColor: farbe, background: `color-mix(in srgb, ${farbe} 8%, var(--bg-card))` }}
                                    onClick={(e) => e.stopPropagation()}
                                    title={`${b.start_time.slice(0, 5)}–${b.end_time.slice(0, 5)}`}
                                  >
                                    ↳ {name ?? ({ show: "Sendung", clock: "Stundenuhr", playlist: "Playlist", live: "Live" } as Record<string, string>)[b.kind] ?? b.kind}
                                  </div>
                                );
                              }
                              return (
                              <div
                                key={b.id}
                                className="mb-1 rounded-md border-l-[3px] px-1.5 py-1 text-xs shadow-sm last:mb-0"
                                style={{ borderColor: farbe, background: `color-mix(in srgb, ${farbe} 14%, var(--bg-card))` }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex items-center justify-between gap-1">
                                  <select
                                    value={b.kind}
                                    onChange={(e) => updateBlockField(b.id, {
                                      kind: e.target.value,
                                      show_id: e.target.value === "show" ? b.show_id ?? shows[0]?.id ?? null : null,
                                      playlist_id: e.target.value === "playlist" ? b.playlist_id ?? playlists[0]?.id ?? null : null,
                                      clock_id: e.target.value === "clock" ? b.clock_id ?? clocks[0]?.id ?? null : null,
                                      traffic_slot_id: e.target.value === "traffic" ? b.traffic_slot_id : null,
                                    })}
                                    className={`${miniSelect} font-medium`}
                                  >
                                    <option value="show">Sendung</option>
                                    <option value="clock">Stundenuhr</option>
                                    <option value="playlist">Playlist</option>
                                    <option value="live">Live</option>
                                  </select>
                                  <button onClick={() => removeBlock(b.id)} className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-muted hover:bg-red-500/10 hover:text-red-600" title="Block entfernen">✗</button>
                                </div>
                                <div className="mt-0.5 font-mono text-[10px] tabular-nums text-muted">{b.start_time.slice(0, 5)}–{b.end_time.slice(0, 5)}</div>
                                {b.kind === "show" && (
                                  <select
                                    value={b.show_id ?? ""}
                                    onChange={(e) => updateBlockField(b.id, { show_id: e.target.value || null })}
                                    className={`mt-1 ${miniSelect}`}
                                  >
                                    <option value="">— Show wählen —</option>
                                    {shows.map((s) => (
                                      <option key={s.id} value={s.id}>{s.title}</option>
                                    ))}
                                  </select>
                                )}
                                {b.kind === "playlist" && (
                                  <select
                                    value={b.playlist_id ?? ""}
                                    onChange={(e) => updateBlockField(b.id, { playlist_id: e.target.value || null })}
                                    className={`mt-1 ${miniSelect}`}
                                  >
                                    <option value="">— Playlist wählen —</option>
                                    {playlists.map((p) => (
                                      <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                  </select>
                                )}
                                {b.kind === "clock" && (
                                  <select
                                    value={b.clock_id ?? ""}
                                    onChange={(e) => updateBlockField(b.id, { clock_id: e.target.value || null })}
                                    className={`mt-1 ${miniSelect}`}
                                  >
                                    <option value="">— Stundenuhr wählen —</option>
                                    {clocks.map((c) => (
                                      <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                  </select>
                                )}
                                {b.kind === "show" && b.show_title && (
                                  <div className="mt-1 truncate text-[10px] font-semibold text-primary" title={b.show_title}>{b.show_title}</div>
                                )}
                                {b.kind === "playlist" && b.playlist_name && (
                                  <div className="mt-1 truncate text-[10px] font-semibold text-primary" title={b.playlist_name}>{b.playlist_name}</div>
                                )}
                              </div>
                              );
                            })}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {renderOutput && (
            <Card title="So sendet der Kanal nach diesem Plan">
              <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg bg-input p-3 text-xs text-primary">
                {renderOutput}
              </pre>
            </Card>
          )}

          {rightsWarnings && (
            <Card
              title="Rechte-Warnungen"
              actions={
                <Badge tone={rightsWarnings.blocked_blocks === 0 ? "success" : "warning"}>
                  <span className="tabular-nums">{rightsWarnings.blocked_blocks} / {rightsWarnings.total_blocks}</span>&nbsp;Slots blockiert
                </Badge>
              }
            >
              {rightsWarnings.blocked_blocks === 0 ? (
                <Callout tone="success">✓ Alle Slots haben gültige Rechte.</Callout>
              ) : (
                <div className="space-y-2">
                  {rightsWarnings.blocks.filter((b) => b.blocking_items.length > 0).map((b) => (
                    <div key={b.id} className="callout callout-warning block text-xs">
                      <div className="font-mono font-medium">
                        {["Mo","Di","Mi","Do","Fr","Sa","So"][b.weekday]} {b.start_time}-{b.end_time} · {b.title ?? "(kein Titel)"}
                      </div>
                      <ul className="ml-4 mt-1 list-disc">
                        {b.blocking_items.map((it, i) => (
                          <li key={i}>
                            <strong>{it.title}</strong>: {it.reasons.join(", ")}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      {!active && (
        <EmptyState
          icon={<CalIcon />}
          title="Noch kein Sendeplan."
          description={'Erstelle einen über "+ Sendeplan".'}
          action={<button onClick={createSchedule} className="btn btn-primary">+ Sendeplan</button>}
        />
      )}
    </Page>
  );
}

function CalIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="h-5 w-5" aria-hidden="true">
      <rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9.5h18M8 3v3M16 3v3" />
    </svg>
  );
}

/** Initialen für den Avatar, wenn kein Foto da ist. */
function initialen(name: string): string {
  const teile = name.trim().split(/\s+/).filter(Boolean);
  return ((teile[0]?.[0] ?? "") + (teile.length > 1 ? teile[teile.length - 1]![0] : "")).toUpperCase() || "?";
}

/** Runder Avatar mit Initialen; Farbe aus dem Namen, damit jede Person „ihre“ Farbe behält. */
function Avatar({ name, size = "h-9 w-9 text-xs" }: { name: string; size?: string }): JSX.Element {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 360;
  return (
    <span
      className={`inline-flex flex-shrink-0 items-center justify-center rounded-full font-semibold text-white ${size}`}
      style={{ background: `linear-gradient(135deg, hsl(${h} 65% 52%), hsl(${(h + 40) % 360} 60% 42%))` }}
      aria-hidden="true"
    >
      {initialen(name)}
    </span>
  );
}

function parseHour(hhmm: string): number {
  const [h] = hhmm.split(":");
  return Number(h) || 0;
}

// ============= Shows =============

export function ShowsPage(): JSX.Element {
  const { slug = "" } = useParams<{ slug: string }>();
  const [shows, setShows] = useState<ShowWithRelations[]>([]);
  const [moderators, setModerators] = useState<Moderator[]>([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", slug: "", moderator_id: "", default_color: "#ff5500" });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.listShows().then(setShows).catch(() => setShows([]));
    api.listModerators().then(setModerators).catch(() => setModerators([]));
  }, []);

  const onCreate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      const sh = await api.createShow({
        title: form.title,
        slug: form.slug || form.title.toLowerCase().replace(/[^a-z0-9-]+/g, "-"),
        default_color: form.default_color,
        moderator_id: form.moderator_id || undefined,
      });
      setShows([sh as ShowWithRelations, ...shows]);
      setAdding(false);
      setForm({ title: "", slug: "", moderator_id: "", default_color: "#ff5500" });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    if (!confirm("Show löschen?")) return;
    await api.deleteShow(id);
    setShows(shows.filter((s) => s.id !== id));
  };

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow="Programm"
        title="Sendungen"
        description="Eure Sendungen – mit Farbe, Moderation und Intro. Die Farbe erscheint im Sendeplan."
        actions={
          <button onClick={() => setAdding((s) => !s)} className={`btn ${adding ? "btn-secondary" : "btn-primary"}`}>
            {adding ? "Abbrechen" : "+ Show"}
          </button>
        }
      />

      {error && <Callout tone="danger" className="mb-4">{error}</Callout>}

      {adding && (
        <form onSubmit={onCreate} className="card mb-6 animate-fade-in p-5">
          <div className="card-title mb-4">Neue Show</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[2fr_1.5fr_1.5fr_auto]">
            <label className="block"><span className="field-label">Titel</span>
              <input className="input" placeholder="Titel" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </label>
            <label className="block"><span className="field-label">Slug</span>
              <input className="input" placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
            </label>
            <label className="block"><span className="field-label">Moderation</span>
              <select className="input" value={form.moderator_id} onChange={(e) => setForm({ ...form, moderator_id: e.target.value })}>
                <option value="">— Moderator —</option>
                {moderators.map((m) => (
                  <option key={m.id} value={m.id}>{m.display_name}</option>
                ))}
              </select>
            </label>
            <label className="block"><span className="field-label">Farbe</span>
              <input type="color" className="h-[2.375rem] w-full cursor-pointer rounded-lg border border-line-strong bg-input p-1 lg:w-16" value={form.default_color} onChange={(e) => setForm({ ...form, default_color: e.target.value })} />
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <button type="submit" className="btn btn-primary">Speichern</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {shows.map((s) => (
          <div key={s.id} className="card group relative overflow-hidden transition hover:shadow-md">
            {/* Farbakzent der Sendung */}
            <div className="h-1.5" style={{ background: s.default_color }} />
            <div className="p-5">
              <div className="flex items-start gap-3">
                <span
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white shadow-sm"
                  style={{ background: s.default_color }}
                  aria-hidden="true"
                >
                  {s.title.slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-primary">{s.title}</div>
                  <div className="truncate font-mono text-xs text-muted">/{s.slug}</div>
                </div>
                <button onClick={() => onDelete(s.id)} className="btn btn-ghost btn-sm text-red-600 opacity-70 hover:opacity-100 dark:text-red-400">Löschen</button>
              </div>
              {s.description_md && <div className="mt-3 line-clamp-3 text-xs leading-relaxed text-secondary">{s.description_md}</div>}
              {(s.moderator || s.intro_audio_title) && (
                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-3 text-xs text-secondary">
                  {s.moderator && (
                    <span className="inline-flex items-center gap-1.5">
                      <Avatar name={s.moderator.display_name} size="h-5 w-5 text-[9px]" />
                      Mod: {s.moderator.display_name}
                    </span>
                  )}
                  {s.intro_audio_title && <Badge tone="neutral">Intro: {s.intro_audio_title}</Badge>}
                </div>
              )}
            </div>
          </div>
        ))}
        {shows.length === 0 && (
          <div className="col-span-full">
            <EmptyState
              icon={<CalIcon />}
              title="Noch keine Shows."
              action={!adding ? <button onClick={() => setAdding(true)} className="btn btn-primary">+ Show</button> : undefined}
            />
          </div>
        )}
      </div>
    </Page>
  );
}

// ============= Moderators =============

export function ModeratorsPage(): JSX.Element {
  const [moderators, setModerators] = useState<Moderator[]>([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ display_name: "", slug: "", rolle: "", gruppe: "", bio_md: "", is_public: true });
  const [error, setError] = useState<string | null>(null);
  // Für die Bildauswahl: der Bildspeicher liefert Pfade, angezeigt werden sie
  // mit der öffentlichen Adresse davor.
  const [publicUrl, setPublicUrl] = useState("");

  useEffect(() => {
    api.listModerators().then(setModerators).catch(() => setModerators([]));
    siteApi.overview().then((o) => setPublicUrl(o.public_url)).catch(() => setPublicUrl(""));
  }, []);

  const onCreate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      const m = await api.createModerator({
        display_name: form.display_name,
        slug: form.slug || form.display_name.toLowerCase().replace(/[^a-z0-9-]+/g, "-"),
        rolle: form.rolle || undefined,
        gruppe: form.gruppe || undefined,
        bio_md: form.bio_md || undefined,
        is_public: form.is_public,
      });
      setModerators([...moderators, m]);
      setAdding(false);
      setForm({ display_name: "", slug: "", rolle: "", gruppe: "", bio_md: "", is_public: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  /** Eine Änderung an einer Person speichern und in der Liste nachziehen. */
  const aendern = async (m: Moderator, patch: Partial<Moderator>): Promise<void> => {
    setError(null);
    try {
      const neu = await api.updateModerator(m.id, patch);
      setModerators((alt) => alt.map((x) => (x.id === m.id ? neu : x)).sort((a, b) => a.position - b.position || a.display_name.localeCompare(b.display_name)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  /** Eine Person um einen Platz verschieben. Die Zahl dahinter sieht niemand. */
  const schieben = async (index: number, richtung: -1 | 1): Promise<void> => {
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= moderators.length) return;
    const a = moderators[index]!;
    const b = moderators[ziel]!;
    // Stehen beide auf derselben Zahl (etwa 0, der Ausgangszustand), hilft
    // Tauschen nicht - dann wird die ganze Liste einmal durchnummeriert.
    const neuListe = [...moderators];
    neuListe.splice(index, 1);
    neuListe.splice(ziel, 0, a);
    setModerators(neuListe.map((m, i) => ({ ...m, position: i })));
    void b;
    try {
      await Promise.all(neuListe.map((m, i) => (m.position === i ? null : api.updateModerator(m.id, { position: i }))));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    if (!confirm("Moderator löschen?")) return;
    await api.deleteModerator(id);
    setModerators(moderators.filter((m) => m.id !== id));
  };

  const onTogglePublic = async (m: Moderator): Promise<void> => {
    await api.updateModerator(m.id, { is_public: !m.is_public });
    setModerators(moderators.map((x) => (x.id === m.id ? { ...x, is_public: !m.is_public } : x)));
  };

  return (
    <Page className="max-w-5xl">
      <PageHeader
        eyebrow="Programm"
        title="Moderatoren"
        description={
          <>
            Die Reihenfolge hier ist die Reihenfolge auf der Hörerseite. Das Bild steht im Querformat –
            wie im Studio fotografiert. Die <b>Funktion</b> ist die kleine Zeile über dem Namen, die
            <b> Gruppe</b> die Zwischenüberschrift, unter der die Person auf der Teamseite steht.
          </>
        }
        actions={
          <button onClick={() => setAdding((s) => !s)} className={`btn ${adding ? "btn-secondary" : "btn-primary"}`}>
            {adding ? "Abbrechen" : "+ Moderator"}
          </button>
        }
      />

      {error && <Callout tone="danger" className="mb-4">{error}</Callout>}

      {adding && (
        <form onSubmit={onCreate} className="card mb-6 animate-fade-in space-y-3 p-5">
          <div className="card-title mb-1">Neue Person</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block"><span className="field-label">Anzeigename</span>
              <input className="input" placeholder="Anzeigename" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} required />
            </label>
            <label className="block"><span className="field-label">Slug</span>
              <input className="input" placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
            </label>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block"><span className="field-label">Funktion</span>
              <input className="input" placeholder="Funktion, z. B. Moderatorin" value={form.rolle} onChange={(e) => setForm({ ...form, rolle: e.target.value })} />
            </label>
            <label className="block"><span className="field-label">Gruppe</span>
              <input className="input" placeholder="Gruppe, z. B. Nachrichtenredaktion" value={form.gruppe} onChange={(e) => setForm({ ...form, gruppe: e.target.value })} />
            </label>
          </div>
          <textarea
            className="input"
            rows={3}
            placeholder="Text für die eigene Seite (Markdown). Ohne Text bekommt die Person keine Seite."
            value={form.bio_md}
            onChange={(e) => setForm({ ...form, bio_md: e.target.value })}
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm text-secondary">
              <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} />
              Öffentlich auf Listener-Site anzeigen
            </label>
            <button type="submit" className="btn btn-primary">Speichern</button>
          </div>
        </form>
      )}

      <div className="card divide-y divide-line overflow-hidden">
        {moderators.map((m, i) => (
          <div key={m.id} className="p-4 transition-colors hover:bg-surface-hover sm:p-5">
            {/* Kopf: Avatar, Name, Zustand – rechts Reihenfolge und Aktionen */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="w-5 text-right text-xs font-medium tabular-nums text-muted" aria-hidden="true">{i + 1}</span>
              <Avatar name={m.display_name} size="h-10 w-10 text-sm" />
              <div className="min-w-0 flex-1 basis-40">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-primary">{m.display_name}</span>
                  {m.is_public
                    ? <Badge tone="success" dot>öffentlich</Badge>
                    : <Badge tone="neutral" dot>nicht öffentlich</Badge>}
                  {m.is_public && !m.bio_md && (
                    <span title="Die Karte im Team verlinkt nur, wenn es etwas zu lesen gibt.">
                      <Badge tone="warning">ohne Text – keine eigene Seite</Badge>
                    </span>
                  )}
                </div>
                <div className="truncate font-mono text-xs text-muted">/{m.slug}</div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <button onClick={() => void schieben(i, -1)} disabled={i === 0} title="Nach oben"
                        className="btn btn-secondary btn-sm btn-icon !w-8 disabled:opacity-30">↑</button>
                <button onClick={() => void schieben(i, 1)} disabled={i === moderators.length - 1} title="Nach unten"
                        className="btn btn-secondary btn-sm btn-icon !w-8 disabled:opacity-30">↓</button>
                <button onClick={() => onTogglePublic(m)} className="btn btn-secondary btn-sm">
                  {m.is_public ? "Privat" : "Öffentlich"}
                </button>
                <button onClick={() => onDelete(m.id)} className="btn btn-ghost btn-sm text-red-600 dark:text-red-400">Löschen</button>
              </div>
            </div>
            {/* Angaben für die Teamseite */}
            <div className="mt-4 grid grid-cols-1 gap-4 sm:pl-[4.75rem] md:grid-cols-[auto_1fr_1fr]">
              <div>
                <span className="field-label">Foto</span>
                <ImageField
                  value={m.photo_url ?? ""}
                  onChange={(v) => void aendern(m, { photo_url: v || null })}
                  publicUrl={publicUrl}
                  label={`Foto von ${m.display_name}`}
                />
              </div>
              <label className="block">
                <span className="field-label">Funktion</span>
                <input
                  className="input"
                  defaultValue={m.rolle ?? ""}
                  placeholder="Moderatorin"
                  onBlur={(e) => e.target.value !== (m.rolle ?? "") && void aendern(m, { rolle: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="field-label">Gruppe</span>
                <input
                  className="input"
                  defaultValue={m.gruppe ?? ""}
                  placeholder="Nachrichtenredaktion"
                  onBlur={(e) => e.target.value !== (m.gruppe ?? "") && void aendern(m, { gruppe: e.target.value })}
                />
              </label>
            </div>
            {m.bio_md && <div className="mt-3 line-clamp-2 text-xs text-muted sm:pl-[4.75rem]">{m.bio_md}</div>}
          </div>
        ))}
        {moderators.length === 0 && (
          <div className="p-4">
            <EmptyState icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="h-5 w-5" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>} title="Noch keine Moderatoren." />
          </div>
        )}
      </div>
    </Page>
  );
}
